/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback } from 'react';
import { SegmentedControl as ChromaSegmentedControl } from '@iag-packages/chroma-react/lib/components';
import styled from 'styled-components';

import THEME from 'theme';

interface SizeProps {
  size: number;
}

const SegmentedControlStackWrapper = styled.div`
  ul {
    border: 0;
    min-width: 100%;
    overflow: visible !important;
    height: auto;

    .c-segmented-control {
      overflow: visible !important;
    }

    .c-segmented-control__item {
      border: none;
      box-sizing: border-box !important;
    }

    label {
      box-sizing: border-box !important;
    }

    .c-segmented-control__input:checked + .c-segmented-control__label {
      background-color: transparent !important;
      border-color: ${THEME.FIELD.BORDER.VALID} !important;
      border-width: ${THEME.FIELD.BORDER.SELECTED};
      color: inherit !important;
    }

    li {
      display: flex;
      height: auto;
      width: 100%;
      margin-bottom: 0.7rem;
      overflow: visible !important;
    }

    li:last-child {
      margin-bottom: 0;
    }

    .c-input {
      padding: 0;
    }

    .c-segmented-control__input + .c-segmented-control__label {
      vertical-align: middle;
      padding: 1em;
      min-width: 0;
      width: 100%;
      border: 1px solid ${THEME.FIELD.BORDER.DEFAULT};
      border-radius: 6px;
    }
  }
`;

const SegmentedControlGridWrapper = styled.div<SizeProps>`
  ul {
    border: 0;
    min-width: 100%;
    display: grid;
    gap: 0.7em;
    grid-template-columns: repeat(
      ${({ size }) => (size < 3 ? size : '3')},
      1fr
    );
    justify-items: stretch;
    overflow: visible !important;
    height: auto;

    .c-segmented-control {
      overflow: visible !important;
    }

    .c-segmented-control__item {
      border: none;
      box-sizing: border-box !important;
    }

    label {
      box-sizing: border-box !important;
    }

    .c-segmented-control__input:checked + .c-segmented-control__label {
      background-color: transparent !important;
      border-color: ${THEME.FIELD.BORDER.VALID} !important;
      border-width: ${THEME.FIELD.BORDER.SELECTED};
      color: inherit !important;
    }

    li {
      display: flex;
      text-align: center;
      align-content: stretch;
      align-items: stretch;
      height: auto;
      width: 100%;
      overflow: visible;
    }

    .c-input {
      padding: 0;
    }

    .c-segmented-control__input + .c-segmented-control__label {
      vertical-align: middle;
      padding: 1em;
      min-width: 0;
      width: 100%;
      border: 1px solid ${THEME.FIELD.BORDER.DEFAULT};
      border-radius: 6px;
    }
  }
`;
type Option = {
  value: unknown;
  label: string;
};

type ChromaSegmentedControlProps = React.ComponentProps<
  typeof ChromaSegmentedControl
> & { values: Option[]; stack: boolean };

const marshal = (value: unknown) => JSON.stringify(value);
const unmarshal = (str: string) => JSON.parse(str);

export const SegmentedControl = ({
  values,
  value,
  stack,
  onChange,
  ...props
}: ChromaSegmentedControlProps) => {
  const wrappedOnChange = useCallback(
    (event) => {
      onChange({
        ...event,
        target: { ...event.target, value: unmarshal(event.target.value) },
      });
    },
    [onChange]
  );

  const segmentedControl = (
    <ChromaSegmentedControl
      required={false}
      values={values.map((option: Option) => ({
        ...option,
        value: marshal(option.value),
      }))}
      value={marshal(value)}
      {...props}
      onChange={wrappedOnChange}
    />
  );

  return stack ? (
    <SegmentedControlStackWrapper>
      {segmentedControl}
    </SegmentedControlStackWrapper>
  ) : (
    <SegmentedControlGridWrapper size={values.length}>
      {segmentedControl}
    </SegmentedControlGridWrapper>
  );
};

export default SegmentedControl;
